import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthGate } from './auth/AuthGate.js';
import { Equipe } from './Equipe.js';
import { Registre } from './Registre.js';
import { Rup } from './Rup.js';
import { Activites } from './Activites.js';
import { Staffing } from './Staffing.js';
import { Metriques } from './Metriques.js';
import { Previsionnel } from './Previsionnel.js';
import { Historique } from './Historique.js';
import { Pointages } from './Pointages.js';
import { HorsStaff } from './HorsStaff.js';
import { DeskList } from './DeskList.js';
import { MailTest } from './MailTest.js';
import { Paiements } from './Paiements.js';
import { Syntheses } from './Syntheses.js';
import { StaffProvider } from './StaffProvider.js';
import { AppCapabilityProvider } from './AppCapabilityProvider.js';
import { AppBrandingProvider } from './AppBrandingProvider.js';
import { AccountMenu, AppBrand, DeskOperatorChip } from '@lemontaigu/platform-ui';
import { AppDeskOperatorProvider } from './AppDeskOperatorProvider.js';
import { sendTestMail, supabase } from './supabase.js';
import { ADMIN_API_BASE } from './config.js';
// ── The PMS group, restored 2026-09-14 (operator) ───────────────────
//
// It was removed on 2026-08-11 because « Plan d'occupation » shipped to
// pms.lemontaigu.com only and the operator came looking for it here: a superset
// that is one tab short is worse than no superset. The removal was explicitly a
// placeholder for a decision about how pages surface across bos.* / reception.*
// / pms.*, not the decision itself.
//
// This is that decision, and it is NOT the old superset rule coming back. The
// group here is deliberately a SUBSET — Planning and Configuration, the two
// surfaces the operator and the desk actually read off the same day — and the
// same two are mounted in reception.*. pms.lemontaigu.com stays the whole PMS:
// Réservations, Calendrier, Occupation, Inventaire, Miroir Vega live there
// and only there (Tarifs left that list on 2026-10-18 — see below).
//
// What makes the subset honest where the old superset was not: the rule is now
// statable in one line — *the rack and the room table follow you into whichever
// cockpit you are already in; everything else is at pms.** — whereas
// « bos.* has everything » was a promise the codebase kept breaking.
//
// Plan is mounted under the name **Planning**, matching the pms.* rename made
// the same day.
//
// Encaissement joined 2026-09-25 (operator), in both cockpits — the desk's
// payment log with its « Départs » sheet. The one-line rule grows by one noun:
// *the rack, the room table and the payment log follow you*. See the reception
// twin for why its print sheet survives the cockpits' `body *` print hide.
//
// Tarifs joined 2026-10-18 (operator), in both cockpits: editable here (bos@
// holds `pms.rate.write`), read-only at the desk. ADR-0019 amendment.
import { Rack } from '@lemontaigu/pms/Rack.js';
import { Configuration } from '@lemontaigu/pms/Configuration.js';
import { Clients } from '@lemontaigu/pms/Clients.js';
import { Encaissement } from '@lemontaigu/pms/Encaissement.js';
import { Tarifs } from '@lemontaigu/pms/Tarifs.js';
import { PointageOta } from '@lemontaigu/pms/PointageOta.js';
import { Carte } from '@lemontaigu/pms/Carte.js';
import { Inventaire } from '@lemontaigu/pms/Inventaire.js';
import { Comptabilite } from '@lemontaigu/pms/Comptabilite.js';
import { AdministrationFiscale } from '@lemontaigu/pms/AdministrationFiscale.js';
import { PerEtablissement, PmsProvider } from '@lemontaigu/pms/PmsProvider.js';
import '@lemontaigu/pms/styles/pms.css';
// Reception modules mounted verbatim (bos-superset decision 2026-07-17): the
// operator works from ONE cockpit. bos@ is in public.staff, so every
// is_staff() read/write these components make works under this session; the
// chat edge functions additionally accept BOS_ALLOWED_ORIGIN. reception.*
// stays deployed unchanged for the desk's hotel@ login.
import { Editor } from '@lemontaigu/reception/restaurant/editor/Editor.js';
import { Printer } from '@lemontaigu/reception/restaurant/printer/Printer.js';
import { Reception } from '@lemontaigu/reception/Reception.js';
import { History } from '@lemontaigu/reception/History.js';
import { Notices } from '@lemontaigu/reception/Notices.js';
import { Review } from '@lemontaigu/reception/Review.js';
import { Labels } from '@lemontaigu/reception/Labels.js';
import { Planning } from '@lemontaigu/reception/team/Planning.js';
import { EffectifsPage } from '@lemontaigu/reception/team/EffectifsPage.js';
import { NavMenu } from '@lemontaigu/reception/nav/NavMenu.js';
import { hardReload, registerServiceWorker, type SwUpdate } from '@lemontaigu/reception/pwa/swUpdate.js';
import { InstallHint } from '@lemontaigu/reception/pwa/InstallHint.js';
// Reception styles first, admin's own second — admin wins shell conflicts.
import '@lemontaigu/reception/styles/index.css';
import '@lemontaigu/reception/styles/editor.css';
import '@lemontaigu/reception/styles/printer.css';
import '@lemontaigu/reception/styles/team.css';
import '@lemontaigu/reception/styles/fr-date.css';
import '@lemontaigu/staff/styles/presence-gantt.css';
import './styles/index.css';

// Back-office (APPS-REORG R1 → bos-superset 2026-07-17) — bos.lemontaigu.com,
// operator-only (bos@ + password + TOTP + is_admin). The full cockpit:
// every reception surface (Restaurant, Chat, Équipe) PLUS the operator-only
// Opérations and RH groups (roster, RUP, activity matrix, payroll, curation). The
// operator's 13"-iPad/desktop target: the nav wraps — all menu items always
// visible, never truncated.
type View =
  | 'editor'
  | 'printer'
  | 'live'
  | 'history'
  | 'notices'
  | 'review'
  | 'labels'
  | 'emails'
  | 'planning'
  | 'effectifs'
  | 'equipe'
  | 'registre'
  | 'rup'
  | 'horsStaff'
  | 'deskList'
  | 'activites'
  | 'staffing'
  | 'metriques'
  | 'previsionnel'
  | 'pointagesHistorique'
  | 'pointages'
  | 'syntheses'
  | 'pmsPlan'
  | 'pmsClients'
  | 'pmsConfiguration'
  | 'pmsEncaissement'
  | 'pmsTarifs'
  | 'pmsPointage'
  | 'pmsInventaire'
  | 'carte'
  | 'paiements'
  | 'comptabilite'
  | 'fiscal';

// Order: Opérations · RH · PMS · Compta · Réception · Restaurant. (PMS was absent between
// 2026-08-11 and 2026-09-14 — see the import-site note.)
//
// OPÉRATIONS vs RH (operator, 2026-07-29) — the old « RH » group mixed two
// different jobs, and the split follows what the tabs are FOR rather than who
// owns them:
//
//   Opérations — running the place day to day. Activités and Planning move
//     here; Planning is the page SHARED with reception@, which is exactly
//     what makes it operational rather than administrative. Synthèses follows
//     the same logic (what the team reported, not how they are employed).
//   RH — pure human-resources ACTIONS on a person's employment: Équipe,
//     Contrats, Registre (RUP), Pointages. All four are legal-record work.
//
// Équipe moved Opérations → RH (operator, 2026-08-06): the page is a view of
// the people files, and it is maintained from Contrats — it belongs beside
// them, not with the day-to-day running of the place.
//
// Planning stopped being a top-level link and became an Opérations tab, which
// is why LINKS is gone entirely — it existed only to place that one link
// between the first group and the rest.
const GROUPS: { label: string; accent: string; tabs: { view: View; label: string }[] }[] = [
  {
    label: 'Opérations',
    accent: '#c9a0dc',
    tabs: [
      { view: 'planning', label: 'Planning' },
      // « Effectifs » sat here from 2026-09-12 — promoted out of the Planning
      // board, filed next to Planning because it is read off the same day. It
      // moved to Restaurant on 2026-09-14 (operator), matching reception.*.
      // Both placements are defensible and the tie is broken by WHOSE sheet it
      // is: the salle and the kitchen carry it every service, so it belongs
      // with the restaurant's other surfaces rather than with the rota it
      // happens to share a date with.
      { view: 'activites', label: 'Activités' },
      { view: 'staffing', label: 'Staffing' },
      { view: 'metriques', label: 'Métriques' },
      { view: 'previsionnel', label: 'Prévisionnel' },
      { view: 'pointagesHistorique', label: 'Historique équipe' },
      { view: 'syntheses', label: 'Synthèses' },
    ],
  },
  {
    label: 'RH',
    accent: '#8ea0c0',
    tabs: [
      { view: 'equipe', label: 'Équipe' },
      { view: 'registre', label: 'Contrats' },
      { view: 'rup', label: 'Registre' },
      { view: 'pointages', label: 'Pointages' },
      // « Hors Staff » (ADR-0024): presence of contract-less trusted people. Beside
      // Pointages because it is the same kiosk, kept apart because it is not payroll.
      { view: 'horsStaff', label: 'Hors Staff' },
      // Who the desk's « Qui êtes-vous ? » shows (ADR-0026): employees and Hors Staff alike.
      { view: 'deskList', label: 'Liste de la réception' },
    ],
  },
  {
    // The PMS surfaces that belong in a cockpit rather than in the PMS — see
    // the import-site note. Same tabs in reception.*, same order; Clients
    // joined 2026-09-29 (operator).
    label: 'PMS',
    accent: '#7a9e8e',
    tabs: [
      { view: 'pmsPlan', label: 'Planning' },
      { view: 'pmsClients', label: 'Clients' },
      { view: 'pmsConfiguration', label: 'Configuration' },
      { view: 'pmsEncaissement', label: 'Encaissement' },
      { view: 'pmsTarifs', label: 'Tarifs' },
      { view: 'pmsInventaire', label: 'Inventaire' },
      // CX2 (PMS-CHANNEX-CUTOVER-BRIEF F3): the extranet against the PMS, around the switch to Channex.
      { view: 'pmsPointage', label: 'Pointage OTA' },
    ],
  },
  {
    // The books side — bos.* only (operator, 2026-10-01). Paiements: every
    // payment of a period by tender, card movements matched to the terminal,
    // for the bank reconciliation. Not mounted in reception.*: the desk has
    // no business with the accounts.
    label: 'Compta',
    accent: '#d4a5a5',
    // Comptabilité (docs/PMS-LEDGER-UI-BRIEF.md, 2026-10-06): the PMS ledger: is the book current, the
    // balance, what each customer owes, and the drill-down to an invoice. The SAME component pms.* mounts
    // (source-exported). Admin only: the screen requires `fin.ledger.read` and says « Droit manquant »
    // without it; the desk never gets it, here or in reception.*.
    tabs: [
      { view: 'paiements', label: 'Paiements' },
      { view: 'comptabilite', label: 'Comptabilité' },
      // « Administration fiscale » (P3 F4c): the same component pms.* mounts; here the operator opens and
      // withdraws an inspector's access (`pms.fiscal.inspector.manage`).
      { view: 'fiscal', label: 'Administration fiscale' },
    ],
  },
  {
    label: 'Réception',
    accent: '#8fd19e',
    tabs: [
      { view: 'live', label: 'Conciergerie' },
      { view: 'history', label: 'Historique' },
      { view: 'notices', label: 'Infos' },
      { view: 'review', label: 'Apprentissage' },
      { view: 'labels', label: 'Étiquettes' },
      // « E-mails » (ADR-0027): the operator's test of the platform's mail sending (Brevo).
      { view: 'emails', label: 'E-mails' },
    ],
  },
  {
    // « Imprimer » left the nav 2026-09-14, matching reception.*: printing is
    // reached by the button inside Menus, which is where you already are when
    // you decide to print. The view stays routable — `#printer` still works and
    // the editor's button still goes there — it is simply not a destination you
    // pick cold.
    //
    // « Effectifs » leads the group, exactly as in reception.* — it came over
    // from Opérations the same day (see the note there). The two cockpits now
    // hold the identical Restaurant group, which is the point: it is one sheet,
    // and the operator and the desk should not have to remember two paths to
    // the same page.
    label: 'Restaurant',
    accent: '#e6b35a',
    tabs: [
      { view: 'effectifs', label: 'Effectifs' },
      { view: 'editor', label: 'Menus' },
      // « Carte » (operator, 2026-10-19, till brief §16): the till's catalogue,
      // the same component and the same place as in reception.*.
      { view: 'carte', label: 'Carte' },
    ],
  },
];

// Every view a nav tab points at, PLUS the ones reachable only from inside a
// page. `printer` is the second kind since 2026-09-14: the editor's print
// button calls `go('printer')`, and `go` writes the hash — so a view missing
// from this list is not merely unlinked, it is UNREACHABLE. `viewFromHash`
// would reject `#printer` on the resulting hashchange and bounce straight back
// to the landing view, a beat after the page had rendered. Deriving the list
// from GROUPS alone is what would cause that, which is the mirror image of the
// bug the pms.* app hit from the opposite direction (a tab in the nav that was
// missing from its VIEWS array).
const VIEWS: View[] = [...GROUPS.flatMap((g) => g.tabs.map((t) => t.view)), 'printer'];
// Landing view, 2026-09-02: **Planning**, matching reception.*. Both cockpits
// open on the board — it is the one screen that answers "what is today", and
// bos.* mounts the same component, so the two now agree about where you start.
const viewFromHash = (): View => {
  const hash = window.location.hash.replace('#', '');
  return (VIEWS as string[]).includes(hash) ? (hash as View) : 'planning';
};

/**
 * The cockpit header. It carried the établissement switcher from 2026-09-14
 * (ADR-0019 §6) until U4 (PMS-UNIFIED-DESK-BRIEF §8): the screens now show the
 * whole reach, and a page about one établissement chooses inside the page.
 */
function Nav({ view, onGo }: { view: View; onGo: (v: View) => void }) {
  return (
      <nav className="app-nav">
        <AppBrand app="Administration" />
        <div className="app-groups">
          {GROUPS.map((g) => (
            <NavMenu key={g.label} label={g.label} accent={g.accent} tabs={g.tabs} view={view} onGo={onGo} />
          ))}
        </div>
        <button type="button" className="pwa-refresh-btn" title="Recharger l’application" aria-label="Recharger l’application" onClick={hardReload}>
          🔄
        </button>
        <DeskOperatorChip />
        <AccountMenu onSignOut={() => void supabase.auth.signOut()} />
      </nav>
  );
}

function App() {
  const [view, setView] = useState<View>(viewFromHash);
  const [swUpdate, setSwUpdate] = useState<SwUpdate | null>(null);

  useEffect(() => {
    const onHash = () => setView(viewFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    registerServiceWorker(setSwUpdate);
  }, []);

  const go = (v: View) => {
    window.location.hash = v;
    setView(v);
  };

  return (
    <AuthGate>
      <AppBrandingProvider>
      <AppCapabilityProvider>
      <AppDeskOperatorProvider>
      {/* `PmsProvider` wraps the SHELL, not just the PMS tabs. The header goes
          in as `chrome`, so it renders immediately while only `<main>` waits on
          the (2-row) property read. Until U4 the switcher in the account menu
          remounted `<main>` through the provider's key; both went with U4
          (PMS-UNIFIED-DESK-BRIEF §8), so nothing remounts the board now. */}
      <PmsProvider
        chrome={
          <>
            {swUpdate && (
              <div className="update-banner">
                <span>Nouvelle version disponible.</span>
                <button type="button" onClick={() => swUpdate.apply()}>
                  Recharger
                </button>
              </div>
            )}
            <InstallHint />
            <Nav view={view} onGo={go} />
          </>
        }
      >
      <main className="app-main">
        {view === 'editor' && <Editor onPrint={() => go('printer')} />}
        {view === 'printer' && <Printer />}
        {view === 'live' && <Reception />}
        {view === 'history' && <History />}
        {view === 'notices' && <Notices />}
        {view === 'review' && <Review />}
        {view === 'labels' && <Labels />}
        {view === 'emails' && <MailTest send={(to) => sendTestMail(ADMIN_API_BASE, to)} />}
        {view === 'planning' && <Planning showHours />}
        {view === 'effectifs' && <EffectifsPage />}
        {/* C3's read ports, bound once — see StaffProvider. */}
        <StaffProvider>
          {view === 'equipe' && <Equipe />}
          {view === 'registre' && <Registre />}
          {view === 'rup' && <Rup />}
          {view === 'activites' && <Activites />}
          {view === 'staffing' && <Staffing />}
          {view === 'metriques' && <Metriques />}
          {view === 'previsionnel' && <Previsionnel />}
          {view === 'pointagesHistorique' && <Historique />}
          {view === 'pointages' && <Pointages />}
          {view === 'horsStaff' && <HorsStaff />}
          {view === 'deskList' && <DeskList />}
          {view === 'syntheses' && <Syntheses />}
        </StaffProvider>
        {view === 'pmsPlan' && <Rack />}
        {view === 'pmsClients' && <Clients />}
        {view === 'pmsConfiguration' && <Configuration />}
        {view === 'pmsEncaissement' && <Encaissement />}
        {view === 'pmsTarifs' && <PerEtablissement><Tarifs /></PerEtablissement>}
        {view === 'pmsInventaire' && <PerEtablissement><Inventaire /></PerEtablissement>}
        {view === 'pmsPointage' && <PerEtablissement><PointageOta /></PerEtablissement>}
        {view === 'carte' && <PerEtablissement><Carte /></PerEtablissement>}
        {view === 'paiements' && <PerEtablissement><Paiements /></PerEtablissement>}
        {view === 'comptabilite' && <PerEtablissement><Comptabilite /></PerEtablissement>}
        {view === 'fiscal' && <PerEtablissement><AdministrationFiscale /></PerEtablissement>}
      </main>
      </PmsProvider>
      </AppDeskOperatorProvider>
      </AppCapabilityProvider>
      </AppBrandingProvider>
    </AuthGate>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
